import React, { useEffect, useState } from "react";
import {
    Button,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import { useDrag, useDrop, DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { showAllUsers, updateUser } from "../../services/authService";
import { useAppSelector } from "../../hooks/storeHooks";
import EditViewModal from "./EditViewModal";
import EditUserModal from "./EditUserModal";
import { deleteUser } from "../../services/authService";
import EditPasswordModal from "./EditPasswordModal";

interface UsersViewProps {
    user: User;
}

// Definimos un tipo para los items que se pueden arrastrar
const ItemTypes = {
    MENU: 'menu'
};

const UsersView: React.FC = () => {

    const { users, isLoading } = useAppSelector((state) => state.UserReducer);
    const [open, setOpen] = useState(false);
    const [openPasswordModal, setOpenPasswordModal] = useState(false);
    const [openUserModal, setOpenUserModal] = useState(false);
    const [selectedMenu, setSelectedMenu] = useState<Menu | null>(null);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [filter, setFilter] = useState("");
    const [viewMode, setViewMode] = useState("table");
    const filteredUsers = users.filter(user => user.username.toLowerCase().includes(filter.toLowerCase()));

    useEffect(() => {
        const fetchUsers = async () => {
            await showAllUsers();
        };
        fetchUsers();
    }, []);

    const handleDeleteUser = async (user: User) => {
        await deleteUser(user._id);
      }

    const moveMenu = (dragIndex: number, hoverIndex: number, user: User) => {
        const newMenus = [...user.menus];
        const draggedMenu = newMenus[dragIndex];

        newMenus.splice(dragIndex, 1);
        newMenus.splice(hoverIndex, 0, draggedMenu);

        const updatedUser = { ...user, menus: newMenus };
        updateUserMenus(updatedUser);
    };

    const updateUserMenus = async (user: User) => {
        try {
            await updateUser(user._id, user);
            console.log("Menus updated successfully");
        } catch (error) {
            console.error("Failed to update menus", error);
        }
    };

    const MenuRow = ({ menu, index, user }: { menu: Menu, index: number, user: User }) => {
        const [{ isDragging }, drag] = useDrag({
            type: ItemTypes.MENU,
            item: { index, user },
            collect: (monitor) => ({
                isDragging: !!monitor.isDragging(),
            }),
        });

        const [, drop] = useDrop({
            accept: ItemTypes.MENU,
            hover: (item: any) => {
                if (item.index !== index) {
                    moveMenu(item.index, index, user);
                    item.index = index;
                }
            },
        });

        return (
            <TableRow
                ref={(node) => drag(drop(node))}
                style={{
                    opacity: isDragging ? 0.5 : 1,
                    cursor: 'move',
                }}
                key={menu.menuName}
            >
                <TableCell>{menu.menuName}</TableCell>
                <TableCell>{menu.views.length} vistas</TableCell>
                <TableCell>
                    <Button
                        variant="contained"
                        onClick={() => {
                            setSelectedMenu(menu);
                            setSelectedUser(user);
                            setOpen(true);
                        }}
                    >
                        Editar
                    </Button>
                </TableCell>
            </TableRow>
        );
    };

    return (
        <DndProvider backend={HTML5Backend}>
            <div className="flex gap-3 flex-col p-3 mt-5 mx-10">
                <h1 className="text-xl">Listado de Usuarios</h1>
                <div className="flex justify-between gap-3">
                    <div className="flex gap-3">
                        <TextField
                            label="Filtrar usuarios"
                            variant="outlined"
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                        />
                    </div>
                    <div>
                        <Button variant="contained" onClick={() => setOpenUserModal(true)} color="primary">
                            Agregar Usuario
                        </Button>
                    </div>
                </div>

                {!isLoading ? filteredUsers.map((user) => (
                    <Card key={user._id} className="p-3 border-2 min-h-[250px]" sx={{ position: "relative" }}>
                        <Typography variant="h6">{user.username}</Typography>
                        <CardContent className="flex gap-3">
                            {viewMode === "cards" ? (
                                <div className="w-2/3" style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
                                    {user.menus.map((menu) => (
                                        <Card
                                            key={menu.menuName}
                                            sx={{ padding: "8px", cursor: "pointer", boxShadow: 1 }}
                                            onClick={() => {
                                                setSelectedMenu(menu);
                                                setSelectedUser(user);
                                                setOpen(true);
                                            }}
                                        >
                                            <CardContent>
                                                <Typography variant="body2">{menu.menuName}</Typography>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </div>
                            ) : (
                                <div className="w-2/3">
                                    <Table>
                                        <TableBody>
                                            {user.menus.map((menu, index) => (
                                                <MenuRow key={menu.menuName} menu={menu} index={index} user={user} />
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>)}

                            <Button
                                sx={{
                                    position: "absolute",
                                    right: "30px",
                                    top: "30px",
                                }}
                                variant="contained"
                                color="secondary"
                                onClick={() => handleDeleteUser(user)}
                            >
                                Eliminar Usuario
                            </Button>

                            <Button
                                sx={{
                                    position: "absolute",
                                    right: "30px",
                                    top: "80px",
                                }}
                                variant="contained"
                                color="primary"
                                onClick={() => {
                                    setSelectedMenu(null);
                                    setSelectedUser(user);
                                    setOpen(true);
                                }}
                            >
                                Agregar Menu
                            </Button>

                            <Button
                                sx={{
                                    position: "absolute",
                                    right: "30px",
                                    top: "130px",
                                }}
                                variant="contained"
                                color="secondary"
                                onClick={() => {
                                    setSelectedMenu(null);
                                    setSelectedUser(user);
                                    setOpenPasswordModal(true);
                                }}
                            >
                                Cambiar Contraseña
                            </Button>
                        </CardContent>
                    </Card>
                )) : <> Cargando ...</>}

                <EditViewModal
                    open={open}
                    onClose={() => {
                        setSelectedMenu(null);
                        setSelectedUser(null);
                        setOpen(false);
                    }}
                    user={selectedUser}
                    selectedMenu={selectedMenu}
                />

                <EditUserModal
                    open={openUserModal}
                    onClose={() => {
                        setSelectedUser(null);
                        setOpenUserModal(false);
                    }}
                    user={selectedUser}
                />

                <EditPasswordModal
                    open={openPasswordModal}
                    onClose={() => {
                        setSelectedUser(null);
                        setOpenPasswordModal(false);
                    }}
                    user={selectedUser!}
                />
            </div>
        </DndProvider>
    );
};

export default UsersView;
